import { RelocationTable } from "@/app/Relocations/Components/RelocationTable";
import { RelocationStatus, RelocationType } from "@/gql/graphql";
import type { TableId } from "@/app/Common/Utils/table-id";

import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { RelocationCreateStepsDialog } from "@/app/Relocations/Components/RelocationCreateStepsDialog";
import { RelocationCreateDialog } from "@/app/Relocations/Components/RelocationCreateDialog";
import { useRelocationCreateDialogPreference } from "@/app/Relocations/Utils/useRelocationCreateDialogPreference";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { relocationStatusCountsQuery } from "@/app/Relocations/GraphQL/relocationStatusCountsQuery";
import { Button } from "@/components/catalyst/button";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
} from "@/components/catalyst/navbar";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { ReactNode } from "react";
import { useTranslations } from "use-intl";
import { RelocationTableState } from "@/app/Relocations/Utils/relocationTableState";

export function RelocationMultiTable({
  supplierId,
  state,
  onStateChange,
  tableId,
  type,
  title,
  createButton,
  extraActions,
}: {
  supplierId?: string;
  state: RelocationTableState;
  onStateChange: (newState: RelocationTableState) => void;
  tableId: TableId;
  type?: RelocationType[];
  title?: string;
  createButton?: ReactNode;
  extraActions?: ReactNode;
}) {
  const { open: openStepsDialog } = useDialog(RelocationCreateStepsDialog);
  const { open: openClassicDialog } = useDialog(RelocationCreateDialog);
  const { useStepsDialog, setUseStepsDialog } =
    useRelocationCreateDialogPreference();

  function openCreateDialog(preferSteps: boolean) {
    const props = {
      initialSupplierId: supplierId,
      onSwitchDialog: () => {
        setUseStepsDialog(!preferSteps);
        openCreateDialog(!preferSteps);
      },
    };

    if (preferSteps) {
      openStepsDialog(props);
      return;
    }

    openClassicDialog(props);
  }
  const t = useTranslations("relocation.table");

  return (
    <div>
      <Header
        supplierId={supplierId}
        onStatusChange={(statuses) =>
          onStateChange({ ...state, statuses, page: 1 })
        }
        statuses={state.statuses}
        type={type}
        title={title}
      />
      <RelocationTable
        id={tableId}
        supplierId={supplierId}
        state={state}
        onStateChange={onStateChange}
        type={type}
        rightButtons={
          <div className="flex items-center gap-2">
            {extraActions}
            {createButton ?? (
              <Button
                LeadingIcon={PlusIcon}
                onClick={() => openCreateDialog(useStepsDialog)}
              >
                {t("addRelocation")}
              </Button>
            )}
          </div>
        }
      />
    </div>
  );
}

function Header({
  supplierId,
  onStatusChange,
  statuses,
  type,
  title,
}: {
  supplierId?: string;
  onStatusChange: (status: RelocationStatus[]) => void;
  statuses: RelocationStatus[];
  type?: RelocationType[];
  title?: string;
}) {
  const { data } = useGqlQuery(relocationStatusCountsQuery, {
    supplierId: supplierId ?? undefined,
    type,
  });
  const t = useTranslations("relocation.table");

  return (
    <div className="relative border-gray-200 pb-5 sm:border-b sm:pb-0">
      <div className="md:flex md:items-center md:justify-between">
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {title ?? t("title")}
        </h3>
      </div>

      <Navbar className="-mb-px">
        <NavbarSection className="max-lg:hidden" id="relocation-navbar">
          {getFilters(t).map(
            (
              { label, statuses: filterStatuses, hideBadge, badgeColor },
              idx,
            ) => {
              const count = filterStatuses.reduce((acc, status) => {
                return (
                  acc +
                  (data?.relocationStatusCounts.find(
                    (counts) => counts.status === status,
                  )?.count ?? 0)
                );
              }, 0);

              const isCurrent =
                statuses?.length === filterStatuses.length &&
                filterStatuses.every((status) => statuses?.includes(status)) &&
                statuses?.every((status) => filterStatuses.includes(status));

              return (
                <NavbarItem
                  key={idx}
                  onClick={() => {
                    onStatusChange?.(filterStatuses);
                  }}
                  current={isCurrent}
                >
                  {label}
                  {count && !hideBadge ? (
                    <Badge className="py-0" color={badgeColor}>
                      {count}
                    </Badge>
                  ) : null}
                </NavbarItem>
              );
            },
          )}
        </NavbarSection>
      </Navbar>
    </div>
  );
}

type Filter = {
  label: ReactNode;
  statuses: RelocationStatus[];
  hideBadge?: boolean;
  badgeColor?: BadgeColor;
};

function getFilters(t: (key: string) => string): Filter[] {
  return [
    {
      label: t("activeAndPaused"),
      statuses: [RelocationStatus.Ready, RelocationStatus.Paused],
      badgeColor: "green",
    },
    {
      label: t("soldOut"),
      statuses: [RelocationStatus.SoldOut],
      badgeColor: "yellow",
    },
    {
      label: t("expired"),
      statuses: [RelocationStatus.Expired],
      badgeColor: "red",
    },

    {
      label: t("draft"),
      statuses: [RelocationStatus.Draft],
    },
    {
      label: t("archived"),
      statuses: [RelocationStatus.Archived],
      hideBadge: true,
    },
  ];
}
